import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { Plus, FolderOpen, Edit2, Trash2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  created_at: string;
}

export default function CategoriesPage() {
  const { profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [categorySlug, setCategorySlug] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!authLoading && profile?.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [profile, authLoading, navigate]);

  useEffect(() => {
    if (profile?.role === 'admin') {
      fetchCategories();
    }
  }, [profile]);

  const fetchCategories = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: 'Errore',
        description: 'Impossibile caricare le categorie',
        variant: 'destructive',
      });
    } else if (data) {
      setCategories(data);
    }
    setLoading(false);
  };

  const handleOpenDialog = (category?: Category) => {
    if (category) {
      setEditingCategory(category);
      setCategoryName(category.name);
      setCategorySlug(category.slug);
      setCategoryDescription(category.description || '');
    } else {
      setEditingCategory(null);
      setCategoryName('');
      setCategorySlug('');
      setCategoryDescription('');
    }
    setShowDialog(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryName || !categorySlug) {
      toast({
        title: 'Errore',
        description: 'Nome e slug sono obbligatori',
        variant: 'destructive',
      });
      return;
    }

    setProcessing(true);
    try {
      if (editingCategory) {
        const { error } = await supabase
          .from('categories')
          .update({
            name: categoryName,
            slug: categorySlug,
            description: categoryDescription || null,
          })
          .eq('id', editingCategory.id);

        if (error) throw error;

        toast({
          title: 'Successo',
          description: 'Categoria aggiornata con successo',
        });
      } else {
        const { error } = await supabase
          .from('categories')
          .insert({
            name: categoryName,
            slug: categorySlug,
            description: categoryDescription || null,
          });

        if (error) throw error;

        toast({
          title: 'Successo',
          description: 'Categoria creata con successo',
        });
      }

      setShowDialog(false);
      fetchCategories();
    } catch (error: any) {
      toast({
        title: 'Errore',
        description: error.message || 'Impossibile salvare la categoria',
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!confirm('Sei sicuro di voler eliminare questa categoria?')) return;

    try {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Successo',
        description: 'Categoria eliminata con successo',
      });

      fetchCategories();
    } catch (error: any) {
      toast({
        title: 'Errore',
        description: error.message || 'Impossibile eliminare la categoria',
        variant: 'destructive',
      });
    }
  };

  if (authLoading || profile?.role !== 'admin') {
    return null;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Categorie Corsi
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Gestisci le categorie disponibili per i corsi
            </p>
          </div>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Nuova categoria
          </Button>
        </div>

        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : categories.length === 0 ? (
              <div className="text-center py-12">
                <FolderOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Nessuna categoria trovata</p>
                <Button onClick={() => handleOpenDialog()} className="mt-4">
                  Crea la prima categoria
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Slug</TableHead>
                      <TableHead>Descrizione</TableHead>
                      <TableHead>Data creazione</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categories.map((category) => (
                      <TableRow key={category.id}>
                        <TableCell className="font-medium">{category.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{category.slug}</Badge>
                        </TableCell>
                        <TableCell className="max-w-xs truncate text-gray-600">
                          {category.description || '-'}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {new Date(category.created_at).toLocaleDateString('it-IT')}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDialog(category)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteCategory(category.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingCategory ? 'Modifica categoria' : 'Nuova categoria'}
              </DialogTitle>
              <DialogDescription>
                {editingCategory ? 'Modifica i dettagli della categoria' : 'Crea una nuova categoria per i corsi'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="category-name">Nome categoria</Label>
                <Input
                  id="category-name"
                  placeholder="Es. Programmazione"
                  value={categoryName}
                  onChange={(e) => {
                    setCategoryName(e.target.value);
                    if (!editingCategory) {
                      setCategorySlug(e.target.value.toLowerCase().replace(/\s+/g, '-'));
                    }
                  }}
                />
              </div>
              <div>
                <Label htmlFor="category-slug">Slug</Label>
                <Input
                  id="category-slug"
                  placeholder="programmazione"
                  value={categorySlug}
                  onChange={(e) => setCategorySlug(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  URL-friendly identifier (es. programmazione, design, business)
                </p>
              </div>
              <div>
                <Label htmlFor="category-description">Descrizione</Label>
                <Textarea
                  id="category-description"
                  placeholder="Descrizione opzionale della categoria..."
                  value={categoryDescription}
                  onChange={(e) => setCategoryDescription(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDialog(false)}
                disabled={processing}
              >
                Annulla
              </Button>
              <Button onClick={handleSaveCategory} disabled={processing}>
                {processing ? 'Salvataggio...' : editingCategory ? 'Aggiorna' : 'Crea'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
